import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import type { CalendarEvent } from 'src/meetings/models';
import { meetingsQueryKey } from 'src/meetings/hooks/useScopedMeetings';
import type { MeetingScope } from 'src/meetings/scope';

export type AddParticipantBody =
  | { participantType: 'CLIENT'; clientId: string }
  | { participantType: 'USER'; userId: string }
  | { participantType: 'EXTERNAL'; name: string; description: string };

function participantsBasePath(eventId: string): string {
  return `calendar-events/${eventId}/participants`;
}

export const useMeetingParticipants = (scope: MeetingScope) => {
  const queryClient = useQueryClient();

  // The scoped list and the single-event detail (useEvent -> ['calendar-events', eventId])
  // are separate query keys, and the family/individual scoped keys don't prefix-match the
  // detail. Invalidate both so the meeting detail refreshes after an edit in every scope.
  const invalidate = (eventId: string) => {
    queryClient.invalidateQueries({ queryKey: meetingsQueryKey(scope) });
    queryClient.invalidateQueries({ queryKey: ['calendar-events', eventId] });
  };

  const addParticipant = useMutation<
    { data: CalendarEvent },
    Error,
    { eventId: string; body: AddParticipantBody }
  >({
    mutationFn: ({ eventId, body }) => api.post<CalendarEvent>(participantsBasePath(eventId), body),
    onSuccess: (_data, { eventId }) => invalidate(eventId),
  });

  const removeParticipant = useMutation<unknown, Error, { eventId: string; participantId: string }>(
    {
      mutationFn: ({ eventId, participantId }) =>
        api.delete(`${participantsBasePath(eventId)}/${participantId}`),
      onSuccess: (_data, { eventId }) => invalidate(eventId),
    },
  );

  return { addParticipant, removeParticipant };
};
